<template>
  <section
    class="premium-lifecycle"
    aria-label="Abonelik geçmişi"
  >
    <div
      v-if="summary"
      class="premium-lifecycle__projection"
    >
      <div class="premium-lifecycle__metric">
        <span>Şu anki erişim</span>
        <strong>{{ accessStateLabel(summary.accessState) }}</strong>
        <small>{{ providerLabel(summary.provider) }} · {{ summary.trial === true ? 'Ücretsiz deneme' : summary.trial === false ? 'Deneme dışında' : 'Deneme durumu bilinmiyor' }}</small>
      </div>
      <div class="premium-lifecycle__metric">
        <span>Mevcut dönem sonu</span>
        <strong>{{ formatTimestamp(summary.validUntil) }}</strong>
        <small>{{ periodBoundaryHelper }}</small>
      </div>
      <div class="premium-lifecycle__metric">
        <span>Otomatik yenileme</span>
        <strong>{{ booleanLabel(summary.autoRenew) }}</strong>
        <small>Bir sonraki dönem için mağazadaki ayar.</small>
      </div>
      <div class="premium-lifecycle__metric">
        <span>{{ renewalMetricLabel }}</span>
        <strong>{{ summary.successfulRenewalCount || 0 }}</strong>
        <small>{{ renewalMetricHelper }}</small>
      </div>
    </div>

    <details class="lifecycle-guide">
      <summary>Bu geçmişi nasıl okumalıyım?</summary>
      <p>Bir mağaza hareketi hem işlem hem erişim kaydı oluşturabilir. Aynı işleme ait olduğu doğrulanan kayıtlar birlikte anlatılır. Her kaydın aslı “Teknik ayrıntılar” bölümünde bulunur.</p>
      <dl class="lifecycle-definition-grid">
        <div><dt>İlk kayıt / düzeltme zamanı</dt><dd>Bilginin sisteme ilk kaydedildiği zaman. Sistem düzeltmeleri bu tarihle gösterilir.</dd></div>
        <div><dt>Kaynak bildirimin olay zamanı</dt><dd>Mağazanın olayı gerçekleştirdiğini bildirdiği zaman. Yoksa ödeme zamanı tahmin edilmez.</dd></div>
        <div><dt>Geçerlilik / dönem sınırı (eski: Etki)</dt><dd>Kaydın ilgili olduğu dönem başlangıcı, bitişi veya durum değişikliği. Her zaman tahsilat tarihi değildir.</dd></div>
        <div><dt>Bildirimin alınması (eski: Alınma)</dt><dd>İlgili mağaza bildiriminin Adverport'a ulaştığı zaman. Otomatik kontrollerde bildirim gelmeyebilir.</dd></div>
        <div><dt>Sistemde son işlenme (eski: Algılama)</dt><dd>Kaydın işlendiği veya daha sonra yeniden kontrol edildiği zaman. İlk fark edilme zamanı veya gecikme süresi olarak okunmamalı.</dd></div>
        <div><dt>Grace / ek ödeme süresi</dt><dd>Ödeme tekrar denenir; kullanıcı Premium'a erişmeye devam eder.</dd></div>
        <div><dt>Hold / ödeme beklemesi</dt><dd>Ödeme beklenirken Premium erişimi kapalıdır. Duraklatma da erişimi kapatır ama farklı bir mağaza durumudur.</dd></div>
        <div><dt>Geçmişten aktarıldı (eski: Backfill)</dt><dd>Daha eski bir kayıt sonradan sisteme eklenmiştir. Aktarım tarihinde yeni bir satın alma yapılmış demek değildir.</dd></div>
        <div><dt>Gecikmeli sıra (eski: Sıra dışı)</dt><dd>Olay, kendisinden daha yeni bir kayıttan sonra işlenmiştir; tek başına hata anlamına gelmez.</dd></div>
      </dl>
    </details>

    <v-alert
      v-if="summary"
      dense
      text
      :type="coverageAlertType"
      class="premium-lifecycle__coverage mt-4 mb-0"
    >
      <strong>{{ coverageStatusLabel(historyDisplayStatus) }}.</strong> {{ coverageAlertText }}
    </v-alert>

    <v-card
      outlined
      class="admin-surface premium-lifecycle__timeline mt-5"
    >
      <div class="premium-lifecycle__section-heading">
        <div>
          <h2>Abonelik geçmişi</h2>
          <small>Ne oldu, ne zaman oldu, erişim nasıl değişti?</small>
        </div>
        <v-btn
          small
          outlined
          color="primary"
          :loading="loading"
          @click="refreshLifecycle()"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiRefresh }}
          </v-icon> Yenile
        </v-btn>
      </div>
      <details
        class="lifecycle-filters"
        :open="hasActiveFilters"
      >
        <summary>Geçmişi filtrele <span v-if="hasPendingFilters">· Seçim henüz uygulanmadı</span><span v-else-if="hasAppliedFilters">· Filtre uygulanıyor</span></summary>
        <div class="premium-lifecycle__filters">
          <v-select
            v-model="filters.category"
            :items="categoryOptions"
            label="Kayıt kategorisi"
            hide-details
            outlined
            dense
            clearable
          ></v-select>
          <v-select
            v-model="filters.eventType"
            :items="eventTypeOptions"
            label="Teknik kayıt türü"
            hide-details
            outlined
            dense
            clearable
          ></v-select>
          <v-text-field
            v-model="filters.from"
            type="date"
            label="Bu tarihten"
            hide-details
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="filters.to"
            type="date"
            label="Bu tarihe kadar"
            hide-details
            outlined
            dense
          ></v-text-field>
          <div class="lifecycle-filter-actions">
            <v-btn
              small
              outlined
              color="primary"
              :loading="loading"
              @click="applyFilters"
            >
              Uygula
            </v-btn>
            <v-btn
              text
              small
              :disabled="!hasActiveFilters && !hasAppliedFilters"
              @click="clearFilters"
            >
              Temizle
            </v-btn>
          </div>
        </div>
        <p class="text-caption mt-3 mb-0">
          Seçim yaptıktan sonra Uygula’ya basın. Filtreler teknik kayıtlara uygulanır. Tarih için mağaza olay zamanı, yoksa geçerlilik, o da yoksa son işlenme kullanılır. Bir hareketin yalnız eşleşen kayıtları görünebilir. Sistem düzeltmeleri de kaynak olay tarihine göre filtrelenir; satırlarında sistemdeki düzeltme zamanı gösterilir.
        </p>
      </details>
      <v-alert
        v-if="errorMessage"
        dense
        outlined
        type="error"
        class="mx-5 mt-2"
      >
        {{ errorMessage }}
      </v-alert>
      <v-progress-linear
        v-if="loading"
        indeterminate
        aria-label="Geçmiş yükleniyor"
      ></v-progress-linear>
      <p
        v-if="!loading && !timelineItems.length"
        class="pa-6 mb-0"
        role="status"
      >
        {{ hasAppliedFilters ? 'Bu filtrelere uyan kayıt yok.' : 'Bu üyelik için henüz geçmiş kaydı bulunmuyor.' }}
      </p>
      <div :aria-busy="loading ? 'true' : 'false'">
        <component
          :is="section.system ? 'details' : 'div'"
          v-for="section in timelineSections"
          :key="section.system ? 'system' : 'movements'"
          :class="section.system ? 'lifecycle-system-notes' : ''"
          :open="section.system && hasAppliedFilters"
        >
          <h3
            v-if="!section.system"
            class="px-6 pt-4"
          >
            Mağaza hareketleri
          </h3>
          <summary v-if="section.system">
            Geçmiş aktarım notları · {{ systemGroups.length }} kayıt
          </summary>
          <p
            v-if="section.system"
            class="text-caption"
          >
            Geçmiş aktarımının kapsamı ve sınırları burada gösterilir. Bu notlar yeni ödeme veya kullanıcının yaptığı ayrı bir işlem anlamına gelmez.
          </p>
          <article
            v-for="group in section.groups"
            :key="group.key"
            class="lifecycle-event"
          >
            <div class="lifecycle-event__time">
              <span
                class="lifecycle-event__dot"
                :class="'lifecycle-event__dot--' + eventColor(group.eventType)"
              ></span>
              <strong>{{ formatTimestamp(group.time.value) }}</strong>
              <small>{{ group.time.label }}</small>
              <small v-if="group.time.estimated">Mağaza olay zamanı kaydedilmemiş.</small>
            </div>
            <div class="lifecycle-event__body">
              <div class="lifecycle-event__heading">
                <h3>{{ group.title }}</h3>
                <v-chip
                  v-if="group.system && group.eventType !== 'HISTORY_BOUNDARY'"
                  x-small
                  outlined
                  color="info"
                >
                  Sistem düzeltmesi
                </v-chip>
                <v-chip
                  v-if="group.backfilled"
                  x-small
                  outlined
                >
                  Geçmişten aktarıldı
                </v-chip>
              </div>
              <p>{{ group.description }}</p>
              <div
                v-if="group.accessBefore || group.accessAfter"
                class="lifecycle-event__access"
              >
                <span>{{ group.system ? 'Sistemdeki erişim:' : 'Erişim:' }}</span>
                <span v-if="group.accessBefore && group.accessBefore !== group.accessAfter">{{ accessStateLabel(group.accessBefore) }} → </span>
                <strong>{{ accessStateLabel(group.accessAfter) }}{{ group.paused ? ' · duraklatma' : '' }}</strong>
                <span v-if="group.accessBefore === group.accessAfter">(değişmedi)</span>
              </div>
              <details class="lifecycle-event__details">
                <summary>Teknik ayrıntılar · {{ group.items.length }} kayıt</summary>
                <div
                  v-if="group.items.length > 1"
                  class="text-caption mb-3"
                >
                  {{ group.system ? 'Aynı sistem düzeltmesinin durum kayıtları birlikte gösteriliyor.' : 'Aynı mağaza işlemine bağlı kayıtlar birlikte gösteriliyor; ayrı ödeme sayılmazlar.' }}
                </div>
                <section
                  v-for="item in group.items"
                  :key="item.rowKey"
                  class="lifecycle-raw-record"
                >
                  <h4>{{ item.kind === 'TRANSACTION' ? 'Mağaza işlemi' : 'Durum kaydı' }} · {{ item.eventType }}</h4>
                  <p class="text-caption">
                    {{ providerLabel(item.provider) }} · {{ item.environment || 'Ortam bilinmiyor' }} · {{ sourceLabel(item) }}
                  </p>
                  <p
                    v-if="item.reason"
                    class="text-caption"
                  >
                    Kaydın özgün açıklaması: {{ item.reason }}
                  </p>
                  <p
                    v-if="item.outOfOrder"
                    class="text-caption"
                  >
                    Gecikmeli sırada işlendi.
                  </p>
                  <dl class="lifecycle-definition-grid">
                    <div><dt>İlk kayıt / düzeltme zamanı</dt><dd>{{ formatTimestamp(item.recordedAt) }}</dd></div>
                    <div><dt>Kaynak bildirimin olay zamanı</dt><dd>{{ formatTimestamp(item.occurredAt) }}</dd></div>
                    <div><dt>Geçerlilik / dönem sınırı</dt><dd>{{ formatTimestamp(item.effectiveAt) }}</dd></div>
                    <div><dt>Bildirimin alınması</dt><dd>{{ formatTimestamp(item.receivedAt) }}</dd></div>
                    <div><dt>Sistemde son işlenme</dt><dd>{{ formatTimestamp(item.detectedAt) }}</dd></div>
                    <div><dt>Kayıttaki dönem başlangıcı</dt><dd>{{ formatTimestamp(item.periodStartAt) }}</dd></div>
                    <div><dt>Kayıttaki dönem sonu</dt><dd>{{ formatTimestamp(item.periodEndAt) }}</dd></div>
                    <div><dt>Erişim kodu (önce → sonra)</dt><dd>{{ item.accessBefore || '-' }} → {{ item.accessAfter || '-' }}</dd></div>
                    <div><dt>Mağaza durumu</dt><dd>{{ item.sourceState || '-' }}</dd></div>
                    <div><dt>Otomatik yenileme (önce → sonra)</dt><dd>{{ timelineBooleanLabel(item.autoRenewBefore) }} → {{ timelineBooleanLabel(item.autoRenewAfter) }}</dd></div>
                    <div><dt>Ücretsiz deneme (önce → sonra)</dt><dd>{{ timelineBooleanLabel(item.trialBefore) }} → {{ timelineBooleanLabel(item.trialAfter) }}</dd></div>
                    <div><dt>Ürün / plan / teklif / ücret fazı</dt><dd>{{ productLabel(item) }}</dd></div>
                    <div><dt>İşlem durumu / iade / tutar</dt><dd>{{ transactionMeta(item) }}</dd></div>
                    <div v-if="item.transactionIdMasked">
                      <dt>Mağaza işlem numarası</dt><dd>
                        <code>{{ item.transactionIdMasked }}</code>
                        <v-btn
                          icon
                          x-small
                          aria-label="Mağaza işlem numarasını kopyala"
                          @click="copyTransactionId(item)"
                        >
                          <v-icon small>
                            {{ icons.mdiContentCopy }}
                          </v-icon>
                        </v-btn>
                      </dd>
                    </div>
                  </dl>
                </section>
              </details>
            </div>
          </article>
        </component>
      </div>
      <div class="lifecycle-pagination">
        <span>{{ page.totalElements }} teknik kayıt · bu sayfada {{ narrativeGroups.length }} hareket / düzeltme<span v-if="systemGroups.length"> · {{ systemGroups.length }} sistem notu</span></span>
        <v-select
          :value="page.size"
          :items="[10, 20, 50]"
          label="Sayfada kayıt"
          outlined
          dense
          hide-details
          @change="changePageSize"
        ></v-select>
        <v-pagination
          v-if="page.totalPages > 1"
          :value="page.number + 1"
          :length="page.totalPages"
          :total-visible="5"
          :disabled="loading"
          @input="changePage"
        ></v-pagination>
      </div>
      <p class="text-caption px-5 pb-4 mb-0">
        Sayfalama kaynak olay / geçerlilik tarihine göredir. Bu sayfada mağaza hareketleri olay zamanı, sistem düzeltmeleri ilk kayıt zamanı ile sıralanır. Yalnız kimliği doğrulanan kayıtlar birleştirilir.
      </p>
    </v-card>

    <details
      v-if="summary && periodsProvided"
      class="lifecycle-guide mt-5"
      @toggle="onPeriodsToggle"
    >
      <summary>Satın alma ve yenileme kayıtları · {{ periodItems.length }} kayıt</summary>
      <p>Satın alma, üyeliğin alınması; yenileme, mevcut üyeliğin uzatılmasıdır. Tarihler abonelik geçmişindeki ilgili mağaza olayına dayanır. Dönem sınırları ayrıca gösterilir.</p>
      <p
        v-if="periodTimelineStatus === 'loading'"
        role="status"
      >
        İlişkili mağaza olayları yükleniyor…
      </p>
      <div
        v-if="['partial', 'error'].includes(periodTimelineStatus)"
        class="mb-4"
      >
        <p role="status">
          İlişkili mağaza geçmişinin tamamı yüklenemedi. Eşleşen olaylar gösteriliyor; boş tarihler kayıt bulunmadığı anlamına gelmez.
        </p>
        <v-btn
          small
          outlined
          @click="loadPeriodEvents"
        >
          Tekrar dene
        </v-btn>
      </div>
      <article
        v-for="(period, index) in periodRecords"
        :key="periodKey(period, index)"
        class="lifecycle-period"
      >
        <div class="lifecycle-period__heading">
          <strong>{{ periodSequenceLabel(period) }}</strong>
          <v-chip
            x-small
            :color="periodStatusColor(period)"
          >
            {{ periodStatusLabel(period) }}
          </v-chip>
        </div>
        <dl class="lifecycle-period__time">
          <dt>{{ period.transactionTime.label }}</dt>
          <dd>{{ period.transactionTime.value ? formatTimestamp(period.transactionTime.value) : periodMissingTimeLabel }}</dd>
        </dl>
        <p class="lifecycle-period__note">
          {{ periodTimeNote(period.transactionTime) }}
        </p>
        <details class="lifecycle-period__details">
          <summary>Dönem sınırları ve mağaza kaydı</summary>
          <dl class="lifecycle-definition-grid mt-4">
            <div v-if="period.transactionTime.source === 'related_event'">
              <dt>İşlem satırındaki olay zamanı</dt><dd>{{ period.occurredAt ? formatTimestamp(period.occurredAt) : 'Boş; yukarıda aynı işleme bağlı mağaza olayının zamanı gösteriliyor.' }}</dd>
            </div>
            <div><dt>Kayıttaki dönem başlangıcı</dt><dd>{{ period.periodStartAt ? formatTimestamp(period.periodStartAt) : 'Kaydedilmemiş' }}</dd></div>
            <div><dt>Kayıttaki dönem sonu</dt><dd>{{ period.periodEndAt ? formatTimestamp(period.periodEndAt) : 'Kaydedilmemiş' }}</dd></div>
            <div><dt>Mağaza</dt><dd>{{ providerLabel(period.provider) }}</dd></div>
            <div><dt>Ürün / plan</dt><dd>{{ periodProductLabel(period) }}</dd></div>
            <div v-if="period.transactionIdMasked">
              <dt>İşlem numarası</dt><dd><code>{{ period.transactionIdMasked }}</code></dd>
            </div>
          </dl>
          <p class="lifecycle-period__note">
            Bir kaydın dönem sonu ile sonraki kaydın dönem başlangıcı aynı olabilir; bu, işlemlerin aynı gün yapıldığını göstermez. Dönem sınırları, aradaki sürede kesintisiz erişim veya her ay tahsilat yapıldığını da kanıtlamaz.
          </p>
        </details>
      </article>
      <p v-if="!periodItems.length">
        Doğrulanmış dönem kaydı bulunmuyor.
      </p>
    </details>

    <details
      v-if="summary"
      class="lifecycle-guide mt-4"
    >
      <summary>Geçmişin kapsamı ve ek bilgiler</summary>
      <dl class="lifecycle-definition-grid">
        <div><dt>Kayıtlı ilk üyelik</dt><dd>{{ formatTimestamp(summary.firstSubscriptionAt) }}</dd></div>
        <div><dt>Son mağaza kontrolü</dt><dd>{{ formatTimestamp(summary.lastStoreVerifiedAt) }}</dd></div>
        <div><dt>Ürün / plan</dt><dd>{{ summaryProductLabel }}</dd></div>
        <div><dt>Ücretsiz denemeden ücretliye geçiş</dt><dd>{{ formatTimestamp(summary.trialConvertedAt) }}</dd></div>
        <div><dt>Kayıtlı yeniden abonelik</dt><dd>{{ summary.successfulResubscriptionCount || 0 }}</dd></div>
        <div><dt>Teknik hold / recovery sayacı</dt><dd>{{ summary.onHoldCount || 0 }} / {{ summary.recoveryCount || 0 }} · Duraklatma ve yeniden açılma kayıtlarını da içerebilir; tahsilat sayısı değildir.</dd></div>
      </dl>
      <div
        v-for="(coverage, index) in displayCoverageRecords"
        :key="coverageKey(coverage, index)"
        class="lifecycle-raw-record"
      >
        <strong>{{ providerLabel(coverage.provider || summary.provider) }} · {{ coverageStatusLabel(coverage.status) }}</strong>
        <dl class="lifecycle-definition-grid">
          <div><dt>Doğrulanan kapsam</dt><dd>{{ coverageRangeLabel(coverage) }}</dd></div>
          <div><dt>Eksik kapsam</dt><dd>{{ missingCoverageLabel(coverage) }}</dd></div>
          <div><dt>Geçmiş aktarım kaynağı</dt><dd>{{ coverageSourceLabel(coverage.source) }}</dd></div>
          <div><dt>Son aktarım</dt><dd>{{ formatTimestamp(coverage.importedAt) }}</dd></div>
          <div><dt>Doğrulanmış işlem sayısı</dt><dd>{{ coverage.verifiedTransactionCount == null ? '-' : coverage.verifiedTransactionCount }}</dd></div>
          <div v-if="coverage.lineageRefMasked">
            <dt>Abonelik zinciri (maskeli)</dt><dd>{{ compactIdentifier(coverage.lineageRefMasked) }}</dd>
          </div>
          <div v-if="coverage.revision || coverage.cursor">
            <dt>Teknik kapsam işaretçisi</dt><dd>{{ compactIdentifier(coverage.revision || coverage.cursor) }}</dd>
          </div>
        </dl>
      </div>
    </details>
    <v-snackbar
      v-model="copySnackbar"
      :timeout="2500"
    >
      {{ copyMessage }}
    </v-snackbar>
  </section>
</template>

<script>
import axios from 'axios'
import {
  mdiContentCopy,
  mdiRefresh,
} from '@mdi/js'
import lifecyclePresentation from './premiumLifecyclePresentation'
import narrative from './premiumLifecycleNarrative'

const EVENT_TYPE_OPTIONS = [
  'STORE_ORDER',
  'PURCHASE',
  'TRIAL_CONVERSION',
  'RENEWAL',
  'RESUBSCRIPTION',
  'REFUND',
  'REVOCATION',
  'SUBSCRIPTION_STARTED',
  'SUBSCRIPTION_RESTARTED',
  'TRIAL_STARTED',
  'TRIAL_CONVERTED',
  'RENEWAL_SUCCEEDED',
  'RENEWAL_FAILED',
  'PAYMENT_FAILED',
  'GRACE_STARTED',
  'GRACE_ENDED',
  'ON_HOLD_STARTED',
  'ON_HOLD_ENDED',
  'PAYMENT_RECOVERED',
  'CANCELLATION_SCHEDULED',
  'CANCELLATION_REVERSED',
  'PAUSE_SCHEDULED',
  'PAUSED',
  'RESUMED',
  'PLAN_CHANGE_SCHEDULED',
  'PLAN_CHANGED',
  'EXPIRED',
  'TRANSACTION_REFUNDED',
  'ENTITLEMENT_REVOKED',
  'HISTORY_BOUNDARY',
]

export default {
  name: 'PremiumLifecycleTimeline',
  props: {
    premiumUserId: {
      type: [Number, String],
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      summary: null,
      coverageRecords: [],
      periodItems: [],
      periodsProvided: false,
      timelineItems: [],
      periodTimelineItems: [],
      periodTimelineStatus: 'unloaded',
      periodRequestSequence: 0,
      periodsOpen: false,
      copySnackbar: false,
      copyMessage: 'İşlem numarası kopyalandı.',
      requestSequence: 0,
      appliedFilters: null,
      filters: {
        category: null,
        eventType: null,
        from: null,
        to: null,
      },
      page: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
      },
      icons: {
        mdiContentCopy,
        mdiRefresh,
      },
      categoryOptions: [
        { text: 'Yenileme', value: 'RENEWAL' },
        { text: 'Erişim değişimi', value: 'ACCESS_CHANGE' },
        { text: 'Ödeme sorunu', value: 'PAYMENT_ISSUE' },
        { text: 'Plan değişikliği', value: 'PLAN_CHANGE' },
        { text: 'Geçmiş aktarımı / sistem notu', value: 'AUDIT' },
      ],

    }
  },
  computed: {
    timelineGroups() { return narrative.groupTimeline(this.timelineItems) },
    narrativeGroups() { return this.timelineGroups.filter(group => group.eventType !== 'HISTORY_BOUNDARY') },
    systemGroups() { return this.timelineGroups.filter(group => group.eventType === 'HISTORY_BOUNDARY') },
    timelineSections() {
      return [
        { system: false, groups: this.narrativeGroups },
        { system: true, groups: this.systemGroups },
      ].filter(section => section.groups.length)
    },
    periodRecords() {
      const groups = narrative.groupTimeline(this.periodTimelineItems)

      return this.periodItems.map(period => ({
        ...period,
        transactionTime: narrative.periodTransactionTime(period, groups),
      }))
    },
    periodMissingTimeLabel() {
      if (this.periodTimelineStatus === 'complete') return 'Eşleşen olay zamanı bulunamadı'
      if (this.periodTimelineStatus === 'loading' || this.periodTimelineStatus === 'unloaded') return 'İlgili olay kontrol ediliyor…'

      return 'İlgili olay yüklenemedi'
    },
    historyCoverageStatus() {
      return lifecyclePresentation.resolveHistoryCoverageStatus(
        this.summary || {},
        this.coverageRecords,
      )
    },
    historyIsComplete() {
      return lifecyclePresentation.isHistoryComplete(
        this.historyCoverageStatus,
        this.summary || {},
        this.coverageRecords,
      )
    },
    historyDisplayStatus() {
      return this.historyCoverageStatus === 'COMPLETE' && !this.historyIsComplete
        ? 'PARTIAL'
        : this.historyCoverageStatus
    },
    coverageAlertType() {
      return this.historyIsComplete ? 'success' : 'warning'
    },
    coverageAlertText() {
      if (this.historyIsComplete) {
        return 'Mağaza işlem geçmişi doğrulandı; yenileme sayısı bu kapsamın toplamıdır. Bu, geçmişteki her iptal veya ödeme bekleme bildiriminin arşivlendiği anlamına gelmez.'
      }
      if (this.historyDisplayStatus === 'PARTIAL') {
        return 'Mağazadan doğrulanabilen kayıtlar gösteriliyor. Daha eski hareketler ve toplam yenileme sayısı eksik olabilir.'
      }
      if (this.historyDisplayStatus === 'BOUNDARY_ONLY') {
        return 'Eski kayıtlar için yalnız bir kapsam sınırı var. Görünen yenileme sayısı tüm üyelik geçmişinin toplamı değildir.'
      }

      return 'Mağazanın tüm geçmişi doğrulanmamış. Toplam yenileme sayısı bilinmiyor.'
    },
    displayCoverageRecords() {
      if (this.coverageRecords.length) {
        return this.coverageRecords.map(coverage => ({
          ...coverage,
          status: coverage.status || this.historyCoverageStatus,
        }))
      }

      return [{
        status: this.historyDisplayStatus,
        source: this.summary?.historySource || (this.summary?.historyCompleteFrom ? 'LEGACY_HISTORY_BOUNDARY' : null),
        coverageStartAt: null,
        coverageEndAt: null,
        missingBeforeAt: this.summary?.historyCompleteFrom || null,
        importedAt: this.summary?.lifecycleBackfilledAt || null,
        provider: this.summary?.provider,
        environment: this.summary?.environment,
      }]
    },
    renewalMetricLabel() {
      return this.historyIsComplete
        ? 'Toplam doğrulanmış yenileme'
        : 'Kayıtlı yenileme'
    },
    renewalMetricHelper() {
      return this.historyIsComplete
        ? 'Tam geçmişte doğrulanmış benzersiz yenilemeler.'
        : 'Doğrulanmış kayıtlar; tüm geçmişin toplamı olmayabilir.'
    },
    eventTypeOptions() {
      return EVENT_TYPE_OPTIONS.map(value => ({
        text: `${this.eventTypeLabel(value)} (${value})`,
        value,
      }))
    },
    hasAppliedFilters() { return Object.values(this.appliedFilters || {}).some(Boolean) },
    hasPendingFilters() {
      return this.appliedFilters !== null
        && Object.keys(this.filters).some(key => (this.filters[key] || null) !== (this.appliedFilters[key] || null))
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(Boolean)
    },
    summaryProductLabel() {
      return [this.summary?.productId, this.summary?.basePlanId, this.summary?.offerId]
        .filter(Boolean)
        .join(' · ') || '-'
    },
    isNextRenewalExpected() {
      return this.summary?.autoRenew === true
        && this.summary?.accessState === 'ACTIVE'
        && this.summary?.trial !== true
    },
    periodBoundaryHelper() {
      if (this.summary?.trial === true && this.summary?.autoRenew === true) {
        return 'Ücretsiz deneme sonu; ödeme başarılı olursa ücretli dönem başlar.'
      }
      if (this.isNextRenewalExpected) {
        return 'Otomatik yenileme açık; mağaza başarılı tahsilat yaparsa bu tarihte yenilenir.'
      }
      if (this.summary?.accessState === 'ACTIVE' && this.summary?.autoRenew === false) {
        return 'Otomatik yenileme kapalı; erişim bu tarihe kadar sürer.'
      }

      return 'Mağazanın bildirdiği dönem sınırı; erişimin açık olduğu anlamına gelmez.'
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        if (value) {
          this.resetAndFetch()
        }
      },
    },
    premiumUserId() {
      if (this.active) {
        this.resetAndFetch()
      }
    },
  },
  beforeDestroy() {
    this.requestSequence += 1
    this.periodRequestSequence += 1
  },
  methods: {
    periodTimeNote(time) {
      if (time.source === 'related_event') return 'Abonelik geçmişindeki aynı mağaza olayının zamanı. Yenileme ve erişim kayıtları birlikte değerlendirildi.'
      if (time.value) return 'Mağazadaki olay zamanı.'

      return 'İşlem satırı ve ilişkili mağaza olayları birlikte kontrol edilir. Dönem sınırları olay tarihi yerine kullanılmaz.'
    },
    clearPeriodEvents() {
      this.periodRequestSequence += 1
      this.periodTimelineItems = []
      this.periodTimelineStatus = 'unloaded'
    },
    async refreshLifecycle() {
      this.clearPeriodEvents()
      await this.fetchLifecycle()
      if (this.periodsOpen) this.loadPeriodEvents()
    },
    onPeriodsToggle(event) {
      this.periodsOpen = event.target.open
      if (this.periodsOpen && this.periodTimelineStatus === 'unloaded') this.loadPeriodEvents()
    },
    async loadPeriodEvents() {
      if (!this.active || !this.premiumUserId || ['loading', 'complete'].includes(this.periodTimelineStatus)) return
      this.periodTimelineStatus = 'loading'
      const sequence = this.periodRequestSequence + 1
      this.periodRequestSequence = sequence
      const { premiumUserId } = this
      try {
        const result = await narrative.loadPeriodTimeline(async (page, size) => {
          if (sequence !== this.periodRequestSequence) throw new Error('Obsolete period request')
          const response = await axios.get(
            `${process.env.VUE_APP_BASE_API_URL}admin/premium/users/${premiumUserId}/lifecycle`,
            { params: { page, size } },
          )

          return response?.data?.data?.timeline
        })
        if (sequence !== this.periodRequestSequence) return
        this.periodTimelineItems = result.items.map(lifecyclePresentation.normalizeTimelineItem)
        this.periodTimelineStatus = result.complete ? 'complete' : 'partial'
      } catch (error) {
        if (sequence === this.periodRequestSequence) this.periodTimelineStatus = 'error'
      }
    },
    changePage(value) { this.onUpdateOptions({ page: value }) },
    changePageSize(value) { this.onUpdateOptions({ page: 1, itemsPerPage: value }) },
    resetAndFetch() {
      this.page.number = 0
      this.summary = null
      this.coverageRecords = []
      this.periodItems = []
      this.periodsProvided = false
      this.timelineItems = []
      this.clearPeriodEvents()
      this.fetchLifecycle()
    },
    async fetchLifecycle() {
      if (!this.active || !this.premiumUserId) {
        return
      }

      const requestSequence = this.requestSequence + 1
      this.requestSequence = requestSequence
      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        this.loading = false
        this.errorMessage = 'Başlangıç tarihi bitiş tarihinden sonra olamaz.'

        return
      }
      this.loading = true
      this.errorMessage = ''
      const requestedFilters = { ...this.filters }
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/premium/users/${this.premiumUserId}/lifecycle`,
          {
            params: {
              page: this.page.number,
              size: this.page.size,
              category: requestedFilters.category || undefined,
              eventType: requestedFilters.eventType || undefined,
              from: this.dateFilterMillis(requestedFilters.from, false),
              to: this.dateFilterMillis(requestedFilters.to, true),
            },
          },
        )

        if (requestSequence !== this.requestSequence) {
          return
        }
        this.appliedFilters = requestedFilters
        this.applyPayload(response?.data?.data)
        if (this.periodsOpen && this.periodTimelineStatus === 'unloaded') this.loadPeriodEvents()
      } catch (error) {
        if (requestSequence === this.requestSequence) {
          this.errorMessage = error?.response?.data?.message || 'Abonelik geçmişi yüklenemedi.'
        }
      } finally {
        if (requestSequence === this.requestSequence) {
          this.loading = false
        }
      }
    },
    applyPayload(payload) {
      const timeline = payload?.timeline || {}
      const rawSummary = payload?.summary || null
      this.summary = rawSummary
        ? lifecyclePresentation.normalizeSummary(rawSummary)
        : null
      const explicitCoverageRows = payload?.historyCoverages
        ?? rawSummary?.historyCoverages
        ?? rawSummary?.coverages
      const coveragePayload = explicitCoverageRows !== null
        && explicitCoverageRows !== undefined
        ? explicitCoverageRows
        : (payload?.coverage || payload?.historyCoverage || rawSummary?.historyCoverage)
      let rawCoverage = []
      if (Array.isArray(coveragePayload)) {
        rawCoverage = coveragePayload
      } else if (coveragePayload) {
        rawCoverage = [coveragePayload]
      }
      this.coverageRecords = rawCoverage.map(this.normalizeCoverage)

      const periodsPayload = payload?.periods
        ?? payload?.subscriptionPeriods
        ?? this.summary?.periods
      const rawPeriods = Array.isArray(periodsPayload)
        ? periodsPayload
        : (periodsPayload?.content || periodsPayload?.items || [])
      this.periodsProvided = periodsPayload !== null && periodsPayload !== undefined
      this.periodItems = rawPeriods
        .map(this.normalizePeriod)
        .sort((first, second) => this.periodSortTime(first) - this.periodSortTime(second))
      this.timelineItems = (timeline.content || []).map((item, index) => ({
        ...lifecyclePresentation.normalizeTimelineItem(item),
        rowKey: `${item.kind || 'EVENT'}-${item.id || `${this.page.number}-${index}`}`,
      }))
      if (timeline.number === 0 && timeline.totalElements === this.timelineItems.length
        && this.appliedFilters && !Object.values(this.appliedFilters).some(Boolean)) {
        this.periodRequestSequence += 1
        this.periodTimelineItems = this.timelineItems
        this.periodTimelineStatus = 'complete'
      }
      this.page = {
        number: Number(timeline.number) || 0,
        size: Number(timeline.size) || this.page.size,
        totalElements: Number(timeline.totalElements) || 0,
        totalPages: Number(timeline.totalPages) || 0,
      }
    },
    normalizeCoverage(coverage) {
      return lifecyclePresentation.normalizeCoverage(coverage)
    },
    normalizePeriod(period) {
      return lifecyclePresentation.normalizePeriod(period)
    },
    periodSortTime(period) {
      return Number(period?.periodStartAt || period?.occurredAt || period?.effectiveAt || 0)
    },
    applyFilters() {
      this.page.number = 0
      this.fetchLifecycle()
    },
    clearFilters() {
      this.filters = {
        category: null,
        eventType: null,
        from: null,
        to: null,
      }
      this.applyFilters()
    },
    onUpdateOptions(options) {
      const nextPage = Math.max((options?.page || 1) - 1, 0)
      const nextSize = options?.itemsPerPage > 0 ? options.itemsPerPage : this.page.size
      if (nextPage === this.page.number && nextSize === this.page.size) {
        return
      }
      this.page.number = this.hasPendingFilters ? 0 : nextPage
      this.page.size = nextSize
      this.fetchLifecycle()
    },
    async copyTransactionId(item) {
      const copyValue = item?.transactionIdCopyValue || item?.transactionIdMasked
      if (!copyValue) {
        return
      }

      this.copyMessage = 'İşlem numarası kopyalanamadı.'
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(copyValue)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = copyValue
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          try {
            textarea.select()
            if (!document.execCommand('copy')) throw new Error('Copy failed')
          } finally {
            document.body.removeChild(textarea)
          }
        }
        this.copyMessage = 'İşlem numarası kopyalandı.'
      } catch (error) {
        this.copyMessage = 'İşlem numarası kopyalanamadı. Tarayıcı iznini kontrol edin.'
      }
      this.copySnackbar = true
    },
    coverageKey(coverage, index) {
      return [
        coverage?.provider,
        coverage?.environment,
        coverage?.lineageRefMasked,
        coverage?.scope,
        index,
      ].filter(value => value !== null && value !== undefined).join('-')
    },
    coverageStatusLabel(value) {
      return lifecyclePresentation.coverageStatusLabel(value)
    },
    coverageSourceLabel(value) {
      return lifecyclePresentation.coverageSourceLabel(value)
    },
    coverageRangeLabel(coverage) {
      return lifecyclePresentation.coverageRangeLabel(
        coverage,
        this.formatTimestamp,
        this.summary?.historyCompleteFrom,
      )
    },
    missingCoverageLabel(coverage) {
      return lifecyclePresentation.missingCoverageLabel(coverage, this.formatTimestamp)
    },
    compactIdentifier(value) {
      return lifecyclePresentation.compactIdentifier(value)
    },
    periodKey(period, index) {
      return `${period?.transactionType || 'PERIOD'}-${index}`
    },
    periodSequenceLabel(period) {
      return lifecyclePresentation.periodSequenceLabel(period, this.historyIsComplete)
        || this.eventTypeLabel(period?.transactionType || period?.eventType)
    },
    periodStatusLabel(period) {
      return lifecyclePresentation.periodStatusLabel(period)
    },
    periodStatusColor(period) {
      return lifecyclePresentation.periodStatusColor(period)
    },
    periodProductLabel(period) {
      return [period?.productId, period?.basePlanId, period?.offerId, period?.billingPhase]
        .filter(Boolean)
        .join(' · ') || '-'
    },
    providerLabel(value) {
      return {
        GOOGLE: 'Google Play',
        ANDROID: 'Google Play',
        APPLE: 'App Store',
        IOS: 'App Store',
        MANUAL: 'Manuel',
      }[value] || value || '-'
    },
    accessStateLabel(value) { return narrative.accessLabel(value) },
    booleanLabel(value) {
      if (value === null || value === undefined) {
        return 'Bilinmiyor'
      }

      return value ? 'Açık' : 'Kapalı'
    },
    timelineBooleanLabel(value) {
      if (value === null || value === undefined) {
        return '-'
      }

      return value ? 'Açık' : 'Kapalı'
    },
    eventTypeLabel(value) { return narrative.eventLabel(value) },
    eventColor(value) {
      if (['PURCHASE', 'TRIAL_CONVERSION', 'RENEWAL', 'RESUBSCRIPTION', 'SUBSCRIPTION_RESTARTED', 'RENEWAL_SUCCEEDED', 'TRIAL_CONVERTED', 'PAYMENT_RECOVERED', 'RESUMED'].includes(value)) {
        return 'success'
      }
      if (['GRACE_STARTED', 'GRACE_ENDED', 'ON_HOLD_STARTED', 'ON_HOLD_ENDED', 'CANCELLATION_SCHEDULED', 'PAUSE_SCHEDULED'].includes(value)) {
        return 'warning'
      }
      if (['RENEWAL_FAILED', 'PAYMENT_FAILED', 'REFUND', 'REVOCATION', 'EXPIRED', 'TRANSACTION_REFUNDED', 'ENTITLEMENT_REVOKED'].includes(value)) {
        return 'error'
      }

      return 'primary'
    },
    sourceLabel(item) {
      return [item?.source, item?.sourceType, item?.sourceSubtype]
        .filter(Boolean)
        .join(' · ') || '-'
    },
    productLabel(item) {
      return [item?.productId, item?.basePlanId, item?.offerId, item?.billingPhase]
        .filter(Boolean)
        .join(' · ') || '-'
    },
    transactionMeta(item) {
      const amount = this.formatCurrency(item?.price, item?.currency)

      return [item?.transactionStatus, item?.refundStatus, amount !== '-' ? amount : null]
        .filter(Boolean)
        .join(' · ') || '-'
    },
    dateFilterMillis(value, endOfDay) { return narrative.dateFilterMillis(value, endOfDay) },
    formatCurrency(value, currency) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      try {
        return new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: currency || 'TRY',
          maximumFractionDigits: 2,
        }).format(Number(value) || 0)
      } catch (error) {
        return `${Number(value) || 0} ${currency || ''}`.trim()
      }
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '-'
      }

      return new Intl.DateTimeFormat('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Istanbul',
      }).format(date)
    },
  },
}
</script>

<style lang="scss" scoped>
.premium-lifecycle { margin-top: 8px; color: var(--premium-text); }
.premium-lifecycle__coverage.warning--text { color: var(--premium-warning-text) !important; }
.premium-lifecycle__projection { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.premium-lifecycle__metric { display: grid; align-content: start; gap: 8px; padding: 20px; border: 1px solid var(--premium-border); border-radius: 12px; background: var(--premium-surface); }
.premium-lifecycle__metric > span { font-size: 0.8rem; color: var(--premium-muted); }
.premium-lifecycle__metric > strong { font-size: 1.05rem; color: var(--premium-strong); }
.premium-lifecycle__metric > small { color: var(--premium-muted); line-height: 1.5; }
.lifecycle-guide { padding: 16px 20px; margin-top: 16px; border: 1px solid var(--premium-border); border-radius: 12px; background: var(--premium-surface); }
summary { cursor: pointer; font-weight: 600; color: var(--premium-text); }
summary:focus-visible { outline: 2px solid var(--v-primary-base); outline-offset: 5px; }
details[open] > summary { margin-bottom: 16px; }
.lifecycle-guide p { margin: 0 0 16px; font-size: 0.85rem; line-height: 1.65; }
.lifecycle-definition-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 24px; }
.lifecycle-definition-grid dt { font-weight: 600; font-size: 0.8rem; color: var(--premium-text); }
.lifecycle-definition-grid dd { margin: 5px 0 0; font-size: 0.82rem; line-height: 1.6; color: var(--premium-muted); overflow-wrap: anywhere; }
.premium-lifecycle__section-heading > .v-btn { color: var(--premium-link) !important; }
.premium-lifecycle__section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 24px; }
.premium-lifecycle__section-heading h2 { font-size: 1.12rem; color: var(--premium-strong); margin-bottom: 5px; }
.premium-lifecycle__section-heading small { color: var(--premium-muted); }
.lifecycle-filters { border-top: 1px solid var(--premium-border); padding: 16px 24px; }
.lifecycle-filter-actions { display: flex; align-items: center; gap: 4px; }
.lifecycle-filter-actions > .v-btn { color: var(--premium-link) !important; }
.lifecycle-filters summary { font-size: 0.85rem; }
.premium-lifecycle__filters { display: grid; grid-template-columns: 1fr 1.5fr 1fr 1fr auto; gap: 12px; align-items: center; }
.lifecycle-event { padding: 24px; display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 24px; border-top: 1px solid var(--premium-border); }
.lifecycle-event__time { position: relative; display: flex; flex-direction: column; gap: 6px; padding-left: 22px; }
.lifecycle-event__time strong { font-size: 0.9rem; }
.lifecycle-event__time small { font-size: 0.73rem; color: var(--premium-muted); }
.lifecycle-event__dot { position: absolute; top: 5px; left: 0; width: 8px; height: 8px; border-radius: 50%; background: var(--premium-muted); }
.lifecycle-event__dot--success { background: #16a34a; }
.lifecycle-event__dot--warning { background: #d97706; }
.lifecycle-event__dot--error { background: #dc2626; }
.lifecycle-event__heading { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.lifecycle-event__heading h3 { font-size: 1rem; color: var(--premium-strong); }
.lifecycle-event__body > p { font-size: 0.85rem; line-height: 1.65; color: var(--premium-muted); margin: 8px 0 12px; }
.lifecycle-event__access { display: flex; flex-wrap: wrap; gap: 5px; font-size: 0.82rem; }
.lifecycle-event__details { margin-top: 16px; }
.lifecycle-event__details > summary { font-size: 0.8rem; color: var(--premium-link); }
.lifecycle-raw-record { margin-top: 16px; padding: 20px; border-radius: 8px; background: var(--premium-subtle); border: 1px solid var(--premium-border); min-width: 0; overflow-wrap: anywhere; }
.lifecycle-raw-record h4 { margin-bottom: 6px; font-size: 0.8rem; }
.lifecycle-raw-record p { color: var(--premium-muted); }
.lifecycle-system-notes { padding: 20px 24px; border-top: 1px solid var(--premium-border); }
.lifecycle-system-notes > summary { font-size: 0.85rem; color: var(--premium-muted); }
.lifecycle-system-notes > p { color: var(--premium-muted); margin-top: 12px; }
.lifecycle-system-notes .lifecycle-event { padding: 20px 0; }
.lifecycle-pagination { border-top: 1px solid var(--premium-border); display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 16px; padding: 20px; }
.lifecycle-pagination > span { margin-right: auto; color: var(--premium-muted); font-size: 0.8rem; }
.lifecycle-pagination .v-input { max-width: 150px; }
.lifecycle-period { border-top: 1px solid var(--premium-border); padding: 20px 0; overflow-wrap: anywhere; }
.lifecycle-period__heading { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
.lifecycle-period__time { margin-top: 16px; }
.lifecycle-period__time dt { font-size: 0.8rem; color: var(--premium-muted); }
.lifecycle-period__time dd { margin-top: 4px; font-weight: 600; color: var(--premium-strong); }
.lifecycle-period__note { font-size: 0.8rem; line-height: 1.65; color: var(--premium-muted); margin: 4px 0 12px; }
.lifecycle-period__details > summary { font-size: 0.8rem; color: var(--premium-link); }
@media (max-width: 1100px) {
  .premium-lifecycle__projection { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .premium-lifecycle__filters { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 700px) {
  .premium-lifecycle__projection, .lifecycle-definition-grid, .premium-lifecycle__filters { grid-template-columns: 1fr; }
  .lifecycle-event { grid-template-columns: minmax(0, 1fr); gap: 16px; padding: 20px; }
  .lifecycle-event__time { padding-left: 18px; }
  .lifecycle-event__time small { display: inline; }
  .premium-lifecycle__section-heading { padding: 20px; }
  .lifecycle-pagination { justify-content: flex-start; }
}
</style>
